import { useEffect } from 'react';

import { useBankFeedTransactionsSummary } from 'domain/bankFeed/queries';
import type { BankFeedSummary } from 'domain/bankFeed/schemas';
import { notifyUserOfError, yieldErrorNotification } from 'lib/toasts';

export type UseBankFeedSummaryResult = {
  summary: BankFeedSummary | null;
  isLoading: boolean;
};

const malformedSummaryToast = {
  id: 'app.generalUnknownError',
  defaultMessage: 'Something went wrong',
};

const useBankFeedSummary = (): UseBankFeedSummaryResult => {
  const { data, isLoading, isError, error } = useBankFeedTransactionsSummary();

  // Effect-based error surface (see useFetchColumnDefs.ts for rationale).
  useEffect(() => {
    if (isError) notifyUserOfError(error);
  }, [isError, error]);

  // A resolved summary missing the Bank (CHECKING) tab is a malformed response —
  // the backend always returns it. computeEmptyVariant is a pure selector and
  // can't surface this, so flag it here with the generic error toast (mirrors the
  // fetch-error path).
  const hasCheckingItem = data == null || data.items.some((i) => i.accountType === 'CHECKING');
  useEffect(() => {
    if (!hasCheckingItem) yieldErrorNotification(malformedSummaryToast);
  }, [hasCheckingItem]);

  // Coerce undefined → null at the boundary so the consumer contract stays
  // `BankFeedSummary | null` (existing `summary == null` checks cover both).
  return {
    summary: data ?? null,
    isLoading,
  };
};

export default useBankFeedSummary;
